import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import {
  libraryCompaniesQueryKeys,
  orgCompanyProfileQueryKeys,
} from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { CreateEntityCompany, EntityCompany } from '@/types/library/Entity'

interface UseCreateCompanyArgs {
  orgId: UUID | ''
  invalidateCompanyProfileOnSuccess?: boolean
}

const createCompany = async (
  orgId: UUID | '',
  data: CreateEntityCompany,
  accessToken: string,
): Promise<EntityCompany> => {
  if (!orgId) {
    throw new Error('Organization ID is required')
  }

  const requestBody = data

  const res = await fetch(`${API_URL}/api/v1/organizations/${orgId}/entities`, {
    method: 'POST',
    body: JSON.stringify(requestBody),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }

  return (await res.json()) as EntityCompany
}

/**
 * Creates a new company entity
 */
export const useCreateCompany = ({
  orgId,
  invalidateCompanyProfileOnSuccess = false,
}: UseCreateCompanyArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()
  const queryClient = useQueryClient()

  return useMutation<EntityCompany, Error, CreateEntityCompany, unknown>({
    mutationFn: (data) => createCompany(orgId, data, accessToken),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: libraryCompaniesQueryKeys.organization({ orgId }),
      })
      if (invalidateCompanyProfileOnSuccess) {
        return queryClient.invalidateQueries({
          queryKey: orgCompanyProfileQueryKeys.organization({ orgId }),
        })
      }
    },
    onError: (error) => {
      handleError(error)
    },
  })
}
